import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Canvas } from "./canvas/Canvas";
import { CommandBar } from "./chrome/CommandBar";
import { ContextMenu } from "./chrome/ContextMenu";
import { FullscreenLayer, fillsTab, useFs } from "./chrome/Fullscreen";
import { InstallTheme } from "./chrome/InstallTheme";
import { ThemePicker } from "./chrome/ThemePicker";
import { CanvasEmpty } from "./onboarding/CanvasEmpty";
import { HelpLayer } from "./help/HelpLayer";
import { Corner } from "./chrome/Corner";
import { OverviewHost, Panels } from "./chrome/Panels";
import { Rail, ZoomControls } from "./chrome/Rail";
import { Toasts } from "./chrome/Toasts";
import { TopBar, TopLeft, TopRight } from "./chrome/TopBar";
import { useTabLook, useTabsOn } from "./chrome/useTabs";
import { Welcome } from "./chrome/Welcome";
import { useShortcuts } from "./chrome/shortcuts";
import { Mark } from "./chrome/Logo";
import { PublicBoard } from "./public/PublicBoard";
import { SHARE_PATH } from "../shared/sharePage";
import { cx } from "./lib/util";
import { currentBoard, set, useStore } from "./state/store";
import { ArchivedBar, ArchivedBoard } from "./boards/Archived";
import { useReportOpen } from "./state/openNow";
import { boot } from "./state/sync";
import { Backdrop } from "./themes/Backdrop";
import { lookOf, resolveTheme, showsPhoto, THEME } from "./themes/themes";
import { signinTheme } from "./chrome/signinTheme";
import { measureTheme } from "./themes/measure";
import { MobileBoard } from "./public/MobileBoard";
import { useChat } from "./agent/store";
import { OAuthConsent } from "./chrome/OAuthConsent";
import { useFoundServersPrompt } from "./state/foundServers";
import { Shell } from "./boards/Shell";
import { RenameHost } from "./boards/Rename";
import { kindOf } from "../shared/boardKinds";
import { BillingLock, useBillingAccess } from "./chrome/BillingLock";
import { phoneSized } from "./phone/size";
import { PhoneChrome } from "./phone/PhoneChrome";
import { PhoneMenu } from "./phone/PhoneMenu";
import { PhoneFull } from "./phone/PhoneFull";
import { usePlanWaiting } from "./boards/plan/waiting";
import { useVoicePrefs } from "./voice/prefs";
import { bootShared, sharedTarget } from "./state/guest";
import { getToken } from "./lib/api";
import { GuestBar, GuestViewBoard } from "./boards/Guest";
import { SharedHome } from "./chrome/SharedHome";
import { DEMO } from "./demo/mode";
import { useBoardAddress } from "./state/navigate";
import { useCustomAgentColors } from "./state/agentColors";

const ChatDock = lazy(() => import("./agent/ChatDock"));
const CodeHost = lazy(() => import("./panels/CustomInspector").then((m) => ({ default: m.CodeHost })));
const VoiceLayer = lazy(() => import("./voice/Layer"));
// the demo (/demo) loads its own parts only there
const DemoBar = lazy(() => import("./demo/DemoBar").then((m) => ({ default: m.DemoBar })));

function useReducedMotion() {
  return useMemo(() => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches, []);
}

function BoardApp() {
  const board = useStore(currentBoard);
  // a plan with questions waiting says how many in the tab's title, so it shows from another tab
  const waiting = usePlanWaiting(board?.id);
  const focus = useStore((s) => s.focusMode);
  const presenting = useStore((s) => s.presenting);
  // a doc, sheet, deck or files board filling the tab: under the bar, or alone while the browser hides its tabs too
  const tabbed = useStore(fillsTab);
  const browserFs = useFs((s) => s.browser);
  const widgetsIn = useFs((s) => s.shelf);
  const mobile = useStore((s) => s.mobile);
  // the boards as tabs: floating where the pill sits, the board starting where it always has (Features 8, David's pick);
  // as a bar across the top, the board's surfaces start under it (Features 7)
  const bar = useTabLook().bar;
  const barred = useTabsOn() && bar && !mobile && !presenting && !!board;
  const panel = useStore((s) => s.panel);
  const chatOpen = useChat((s) => s.open);
  const codeOpen = useStore((s) => !!s.codeFor);
  // voice loads only on a computer where it was turned on
  const voiceOn = useVoicePrefs((s) => s.on);
  const reduced = useReducedMotion();
  // a typed board (a doc, a mind map, a calendar) draws its own surface in the canvas's place
  const typed = !!board && kindOf(board) !== "canvas";
  // an archived board opens read only, with its bar and Restore (Features 1, 20 Oct)
  const archived = !!board?.archived;
  // made themes arrive and change over the socket; a preview is worn while someone looks at it
  useStore((s) => s.themes);
  const preview = useStore((s) => s.themePreview);
  const theme = resolveTheme(board ? (preview ? lookOf(board.theme, preview) : board.theme) : undefined, { reducedMotion: reduced });
  // agents' colors the user chose, drawn as the named ones are (Features 6)
  const agentTints = useCustomAgentColors(theme.def);
  useShortcuts();
  // each board and doc page at an address of its own, Back and Forward between them (Fixes 10)
  useBoardAddress();
  useFoundServersPrompt();
  // what is open here, for agents outside the board (Features 1, 20 Oct)
  useReportOpen();
  // a worn theme's picture is measured once: its tone, and its colors when it left them to the picture
  const worn = preview ? null : theme.def.custom;
  useEffect(() => {
    void measureTheme(worn);
  }, [worn?.id, worn?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme.def.bg);
    document.title = board ? `${waiting ? `(${waiting}) ` : ""}${board.name}` : "Context Board";
  }, [theme.def.bg, board?.name, waiting]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onResize = () => set({ mobile: phoneSized() });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // the board never scrolls as a page; if focus or a browser nudges it, put it back
  useEffect(() => {
    const root = document.querySelector<HTMLElement>(".board-root");
    if (!root) return;
    const snap = () => {
      if (root.scrollTop || root.scrollLeft) root.scrollTo(0, 0);
    };
    root.addEventListener("scroll", snap, { passive: true });
    return () => root.removeEventListener("scroll", snap);
  }, []);
  return (
    <div className={cx("board-root", focus && "chrome-hidden", barred && "has-tabbar", presenting && "is-presenting", tabbed && "is-fs-tab", tabbed && browserFs && "is-fs-browser", tabbed && widgetsIn && "has-fs-shelf", chatOpen && "chat-open", typed && "is-typed")} data-mode={theme.def.mode} data-pixel={theme.def.pixel ? "1" : undefined} data-photo={showsPhoto(theme.def, board?.theme.backdrop) ? "1" : undefined} data-theme={theme.def.id} style={{ ...theme.vars, ...agentTints.vars } as React.CSSProperties}>
      {agentTints.css && <style>{agentTints.css}</style>}
      <Backdrop def={theme.def} motion={theme.motion} dim={theme.dim} custom={board?.theme.backdrop ?? null} pick={board?.theme.bg ?? 0} />
      {mobile ? (
        <>
          <MobileBoard readOnly={archived} shelved={archived} />
          {archived && <ArchivedBar board={board!} phone />}
          {panel && panel !== "boards" && <Panels />}
          <PhoneMenu />
          <PhoneFull />
          <PhoneChrome />
        </>
      ) : (
        <>
          {archived ? <ArchivedBoard board={board!} /> : typed ? <Shell board={board} /> : <Canvas />}
          <TopLeft />
          <TopBar />
          <TopRight />
          {!typed && !archived && <Rail />}
          {!typed && <ZoomControls simple={archived} />}
          <CommandBar />
          {!typed && !archived && <CanvasEmpty />}
          {!typed && !archived && <Corner />}
          <Panels />
          <ContextMenu />
          <FullscreenLayer />
          <ThemePicker />
          <InstallTheme />
          {codeOpen && (
            <Suspense fallback={null}>
              <CodeHost />
            </Suspense>
          )}
        </>
      )}
      <OverviewHost />
      {/* a board renamed from All boards, its right-click menu, a phone or the command bar (Fixes 9) */}
      <RenameHost />
      {voiceOn && (
        <Suspense fallback={null}>
          <VoiceLayer />
        </Suspense>
      )}
      {chatOpen && (
        <Suspense fallback={null}>
          <ChatDock />
        </Suspense>
      )}
      {/* tips, hints, where you left off, and the corner's cards where the board has no corner (Features 4) */}
      {!archived && <HelpLayer corner={!mobile && !typed} />}
      <Toasts />
    </div>
  );
}

/**
 * A board someone shared with this person (Features 1, sharing): the board alone, live, with the bar that says whose
 * it is. Can edit: the board's own surface and its widgets' settings. Can view: as a shared link draws it. None of the
 * owner's chrome (chat, connect, settings, other boards) is here.
 */
function GuestApp() {
  const board = useStore(currentBoard);
  const guest = useStore((s) => s.guest)!;
  const mobile = useStore((s) => s.mobile);
  const reduced = useReducedMotion();
  useStore((s) => s.themes);
  const theme = resolveTheme(board?.theme, { reducedMotion: reduced });
  const edit = guest.role === "edit";
  const typed = !!board && kindOf(board) !== "canvas";
  // full screen under its bar, as for the owner (Features 7): the bar without the tools that change the board
  const tabbed = useStore(fillsTab);
  const browserFs = useFs((s) => s.browser);
  useShortcuts();
  useEffect(() => {
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme.def.bg);
    document.title = board ? `${board.name} · shared with you` : "Context Board";
  }, [theme.def.bg, board?.name]);
  useEffect(() => {
    const onResize = () => set({ mobile: phoneSized() });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  if (!board) return null;
  return (
    <div className={cx("board-root is-guest", typed && "is-typed", tabbed && "is-fs-tab", tabbed && browserFs && "is-fs-browser")} data-mode={theme.def.mode} data-pixel={theme.def.pixel ? "1" : undefined} data-photo={showsPhoto(theme.def, board.theme.backdrop) ? "1" : undefined} data-theme={theme.def.id} style={theme.vars as React.CSSProperties}>
      <Backdrop def={theme.def} motion={theme.motion} dim={theme.dim} custom={board.theme.backdrop ?? null} pick={board.theme.bg ?? 0} />
      {mobile ? (
        <>
          {/* on a phone a shared board opens to view; changing it is for a computer, for now */}
          <MobileBoard readOnly title={board.name} />
          <GuestBar phone />
          <PhoneChrome />
        </>
      ) : (
        <>
          {edit ? typed ? <Shell board={board} /> : <Canvas /> : <GuestViewBoard board={board} />}
          <GuestBar />
          {!typed && <ZoomControls simple={!edit} />}
          {edit && <Panels />}
          {edit && <ContextMenu />}
          <FullscreenLayer />
        </>
      )}
      <Toasts />
    </div>
  );
}

export function App() {
  const phase = useStore((s) => s.phase);
  const code = useStore((s) => s.bootErrorCode);
  const boardId = useStore((s) => s.boardId);
  // a link's page part (/s/<link>/<page>) is a page of a web page's site (S4)
  const publicSlug = useMemo(() => SHARE_PATH.exec(location.pathname)?.[1] ?? null, []);
  // a board shared with this person (Features 1, sharing): ?shared=<ws>.<board>
  const shared = useMemo(sharedTarget, []);
  const guest = useStore((s) => !!s.guest);
  // an AI app asking to connect: its own page, without loading a board
  const consent = useMemo(() => location.pathname === "/oauth/authorize", []);
  const billingError = code === "billing_required" || code === "trial_required";
  const billing = useBillingAccess(!DEMO && !publicSlug && !consent && !shared && (phase === "ready" || (phase === "error" && billingError)), boardId);
  useEffect(() => {
    // the demo opens its sample, and never asks the board's server for anything (src/demo)
    if (DEMO) return void import("./demo/boot").then((m) => m.bootDemo());
    if (!publicSlug && !consent) void (shared ? bootShared(shared) : boot());
  }, [publicSlug, consent, shared]);
  if (consent) return <OAuthConsent />;
  if (publicSlug) return <PublicBoard slug={publicSlug} />;
  if (phase === "error" && billingError) return <BillingLock {...billing} />;
  if (phase === "boot")
    return (
      <BootScene>
        <span className="boot-mark" role="status" aria-label="Opening your board">
          <span className="boot-tiles" aria-hidden>
            <Mark size={44} />
          </span>
          <span className="boot-name">Context Board</span>
        </span>
      </BootScene>
    );
  if (phase === "welcome") return <Welcome />;
  if (phase === "error") return <BootError />;
  if (phase === "shared") return <SharedHome />;
  if (guest) return <GuestApp />;
  if (DEMO)
    return (
      <>
        <Suspense fallback={null}>
          <DemoBar />
        </Suspense>
        <BoardApp />
      </>
    );
  return <BoardApp />;
}

/** Opening and failing to open wear the sign-in page's scene for the day, so it never flashes a colour of its own. */
function BootScene({ children }: { children: React.ReactNode }) {
  const [scene] = useState(signinTheme);
  return (
    <div className="boot">
      <Backdrop def={THEME[scene]} motion={false} dim={0.3} clearTop />
      <div className="boot-body">{children}</div>
    </div>
  );
}

function BootError() {
  const err = useStore((s) => s.bootError);
  return (
    <BootScene>
      <div className="boot-error">
        <Mark size={30} />
        <p className="boot-error-title">{DEMO ? "The demo did not open." : sharedTarget() ? "The shared board did not open." : "The board could not load."}</p>
        {err && <p>{err}</p>}
        {DEMO ? null : sharedTarget() ? (
          <p className="boot-error-safe">
            <a href="/">Back to {getToken() ? "your boards" : "Shared with you"}</a>
          </p>
        ) : (
          <p className="boot-error-safe">Nothing has been lost. Your board is saved on our side.</p>
        )}
        <button className="btn btn-primary" onClick={() => location.reload()}>
          Try again
        </button>
      </div>
    </BootScene>
  );
}
